import { Injectable } from '@angular/core';
import { appendSortedNode } from '@models/apollo';
import {
  CreateOrganizationInput,
  CreateOrganizationMutation,
  InviteOrganizationMemberInput,
  InviteOrganizationMemberMutation,
  LoadInstructorOrganizationListQuery,
  LoadOrganizationAutomationQuery,
  LoadOrganizationDetailsQuery,
  LoadOrganizationListQuery,
  LoadOrganizationListWithRevenueQuery,
  LoadOrganizationMembershipsQuery,
  LoadOrganizationPermissionsQuery,
  LoadOrganizationSsoProvidersQuery,
  LoadOrganizationStripeQuery,
  LoadOrganizationWithRevenueQuery,
  UpdateOrganizationInput,
  UpdateOrganizationMutation
} from '@queries/generated';
import { finalMap } from '@rx/final-map';
import { startOf } from '@util/date';
import uniqBy from 'lodash/uniqBy';
import { combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';

const PER_PAGE = 250;

@Injectable({
  providedIn: 'root'
})
export class OrganizationsService {
  list$ = (() => {
    const variables = { first: PER_PAGE };
    const listQuery = this.loadOrganizationListQuery.watch(variables);
    return listQuery.valueChanges.pipe(
      map(({ data }) => data.organizations.all),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          const after = pageInfo.endCursor;
          listQuery.fetchMore({ variables: { ...variables, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
  })();

  listForInstructor$ = (() => {
    const variables = { first: PER_PAGE };
    const listQuery = this.loadInstructorOrganizationListQuery.watch(variables);
    const instructorList$ = listQuery.valueChanges.pipe(
      map(({ data }) => data.organizations.allForInstructor),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          const after = pageInfo.endCursor;
          listQuery.fetchMore({ variables: { ...variables, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
    return combineLatest([this.list$, instructorList$]).pipe(
      map(([all, allForInstructor]) => uniqBy([...all, ...allForInstructor], 'id'))
    );
  })();

  constructor(
    private loadOrganizationListQuery: LoadOrganizationListQuery,
    private loadOrganizationListWithRevenueQuery: LoadOrganizationListWithRevenueQuery,
    private loadInstructorOrganizationListQuery: LoadInstructorOrganizationListQuery,
    private loadOrganizationDetailsQuery: LoadOrganizationDetailsQuery,
    private createOrganizationMutation: CreateOrganizationMutation,
    private updateOrganizationMutation: UpdateOrganizationMutation,
    private loadOrganizationAutomationQuery: LoadOrganizationAutomationQuery,
    private loadOrganizationMembershipsQuery: LoadOrganizationMembershipsQuery,
    private inviteOrganizationMemberMutation: InviteOrganizationMemberMutation,
    private loadOrganizationPermissionsQuery: LoadOrganizationPermissionsQuery,
    private loadOrganizationWithRevenueQuery: LoadOrganizationWithRevenueQuery,
    private loadOrganizationStripeQuery: LoadOrganizationStripeQuery,
    private loadSSOProvidersQuery: LoadOrganizationSsoProvidersQuery
  ) {}

  find = (id: number) => {
    return this.loadOrganizationDetailsQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.organizations.findById));
  };

  loadPermissions = (gid: string) => {
    return this.loadOrganizationPermissionsQuery
      .watch({ gid })
      .valueChanges.pipe(map(({ data }) => data.organizations.findByGid.can));
  };

  create(input: CreateOrganizationInput) {
    return this.createOrganizationMutation
      .mutate(
        { input },
        {
          update: (store, { data: { createOrganization } }) => {
            if (createOrganization.errors.length > 0) {
              return;
            }

            store.modify({
              id: 'OrganizationsNamespace:{}',
              fields: {
                all: appendSortedNode(createOrganization.organization, 'name')
              }
            });
          }
        }
      )
      .pipe(
        map(({ data }) => data.createOrganization),
        finalMap('organization')
      );
  }

  update(input: UpdateOrganizationInput) {
    return this.updateOrganizationMutation.mutate({ input }).pipe(
      map(({ data }) => data.updateOrganization),
      finalMap('organization')
    );
  }

  loadAutomationDetails = (id: number) => {
    return this.loadOrganizationAutomationQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.organizations.findById));
  };

  loadMemberships = (id: number) => {
    return this.loadOrganizationMembershipsQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.organizations.findById.memberships));
  };

  inviteMember(input: InviteOrganizationMemberInput) {
    return this.inviteOrganizationMemberMutation
      .mutate({ input })
      .pipe(map(({ data }) => data.inviteOrganizationMember.ok));
  }

  listWithRevenue(currency: string, startDate: Date = startOf('month'), endDate: Date = new Date()) {
    return this.loadOrganizationListWithRevenueQuery
      .watch({ currency, startDate, endDate })
      .valueChanges.pipe(map(({ data }) => data.organizations.allForTheLedger.nodes));
  }

  loadWithRevenue(gid: string, currency: string, startDate: Date = startOf('month'), endDate: Date = new Date()) {
    return this.loadOrganizationWithRevenueQuery
      .watch({ gid, currency, startDate, endDate })
      .valueChanges.pipe(map(({ data }) => data.organizations.findByGid));
  }

  loadStripeDetails = (id: number) => {
    return this.loadOrganizationStripeQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.organizations.findById));
  };

  loadSSOProviders = (id: number) => {
    return this.loadSSOProvidersQuery.watch({ id }).valueChanges.pipe(map(({ data }) => data.organizations.findById));
  };
}
